import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useTheme, ACCENT_COLORS, THEMES } from '@/hooks/useTheme';
import { useAuth } from '@/hooks/useAuth';
import { ThemeDesigner } from '@/components/ThemeDesigner';
import { ChangePasswordForm } from '@/components/ChangePasswordForm';
import { Sun, Moon, Monitor, Check, Contrast, Smartphone, Zap, Palette, MonitorPlay, KeyRound, GlassWater } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getThemeIcon = (iconName: string) => {
  switch (iconName) {
    case 'Sun': return <Sun className="w-4 h-4" />;
    case 'Moon': return <Moon className="w-4 h-4" />;
    case 'Monitor': return <Monitor className="w-4 h-4" />;
    case 'Contrast': return <Contrast className="w-4 h-4" />;
    case 'Smartphone': return <Smartphone className="w-4 h-4" />;
    case 'Zap': return <Zap className="w-4 h-4" />;
    case 'GlassWater': return <GlassWater className="w-4 h-4" />;
    default: return <Sun className="w-4 h-4" />;
  }
};

export const SettingsDialog: React.FC<SettingsDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { theme, setTheme, accentColor, setAccentColor } = useTheme();
  const { changePassword } = useAuth();
  const [themeDesignerOpen, setThemeDesignerOpen] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  const handleOpenKiosk = () => {
    window.open('/?kiosk=true', '_blank');
  };

  const handlePasswordChange = async (newPassword: string) => {
    const result = await changePassword(newPassword);
    if (!result.error) {
      toast.success('Password updated successfully!');
      setShowPasswordChange(false);
    }
    return result;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
          <DialogDescription>
            Customize your workspace appearance
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Password Change */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Account Security</Label>
            {showPasswordChange ? (
              <div className="border rounded-lg p-4 bg-muted/30">
                <ChangePasswordForm
                  onChangePassword={handlePasswordChange}
                  onCancel={() => setShowPasswordChange(false)}
                  compact={true}
                />
              </div>
            ) : (
              <Button
                variant="outline"
                className="w-full justify-start gap-2 h-auto py-3"
                onClick={() => setShowPasswordChange(true)}
              >
                <KeyRound className="w-5 h-5 text-primary" />
                <div className="text-left">
                  <div className="text-sm font-medium">Change Password</div>
                  <div className="text-xs text-muted-foreground">
                    Update your account password
                  </div>
                </div>
              </Button>
            )}
          </div>

          {/* Kiosk Mode */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Kiosk Mode</Label>
            <Button
              variant="outline"
              className="w-full justify-start gap-2 h-auto py-3"
              onClick={handleOpenKiosk}
            >
              <MonitorPlay className="w-5 h-5 text-primary" />
              <div className="text-left">
                <div className="text-sm font-medium">Open Kiosk Mode</div>
                <div className="text-xs text-muted-foreground">
                  Simplified interface for label printing with barcode scanner support
                </div>
              </div>
            </Button>
          </div>

          {/* Theme Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Theme</Label>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1.5"
                onClick={() => setThemeDesignerOpen(true)}
              >
                <Palette className="w-3.5 h-3.5" />
                Custom Theme
              </Button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {THEMES.map((t) => (
                <Button
                  key={t.id}
                  variant={theme === t.id ? 'default' : 'outline'}
                  size="sm"
                  className="flex flex-col gap-1 h-auto py-2 px-2"
                  onClick={() => setTheme(t.id as any)}
                >
                  {getThemeIcon(t.icon)}
                  <span className="text-[10px]">{t.name}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Accent Color */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Accent Color</Label>
            <div className="grid grid-cols-4 gap-2">
              {ACCENT_COLORS.map((color) => (
                <button
                  key={color.name}
                  onClick={() => setAccentColor(color.name)}
                  className={cn(
                    "relative h-10 rounded-md transition-all border-2",
                    accentColor === color.name
                      ? "border-foreground scale-105"
                      : "border-transparent hover:scale-105"
                  )}
                  style={{ backgroundColor: `hsl(${color.hsl})` }}
                  title={color.name}
                >
                  {accentColor === color.name && (
                    <Check className="absolute inset-0 m-auto w-5 h-5 text-white drop-shadow-md" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Theme Designer */}
      <ThemeDesigner open={themeDesignerOpen} onOpenChange={setThemeDesignerOpen} />
    </Dialog>
  );
};
